import { SaleListingSearchSchema } from '@fastre/core/schemas/typesense'
import { ArrowBackRounded, ArrowForwardRounded } from '@mui/icons-material'
import { Box, IconButton, Stack, Typography } from '@mui/joy'
import { useApi } from 'api'
import Loading from 'components/Loading'
import { MobileFriendlyStack } from 'components/stack'
import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns'
import { prop } from 'ramda'
import { useEffect, useState } from 'react'
import MainTable from './mainTable'
import MonthSummary from './monthSummary'
import SummarySection from './summarySection'
import { ListingsSummary } from './types'

export default function SalesBoard() {
    const api = useApi()

    const getListingsForBothMonths = async (
        currentMonth: Date,
    ): Promise<{ thisMonth: ListingsSummary; lastMonth: ListingsSummary }> => {
        const lastMonth = addMonths(currentMonth, -1)
        const searchProps: Partial<SaleListingSearchSchema> = {
            limit: 250,
            page: 1,
            customFilters: [
                `(statusLog-offline:>=${startOfMonth(lastMonth).getTime()} && statusLog-offline:<=${endOfMonth(currentMonth).getTime()}) || (contract.contractDate:>=${startOfMonth(lastMonth).getTime()} && contract.contractDate:<=${endOfMonth(currentMonth).getTime()})`,
            ],
            sortDirection: 'asc',
        }
        const listings = await api
            .post('listings/sale/search', searchProps)
            .then(({ data }) => data.hits.map(prop('document')))

        const thisMonthUnderContract = listings.filter(
            listing =>
                listing.contract?.contractDate &&
                listing.contract.contractDate >= startOfMonth(currentMonth).getTime() &&
                listing.contract.contractDate <= endOfMonth(currentMonth).getTime(),
        )

        const thisMonthNewListings = listings.filter(
            listing =>
                listing.statusLog?.offline &&
                listing.statusLog.offline >= startOfMonth(currentMonth).getTime() &&
                listing.statusLog.offline <= endOfMonth(currentMonth).getTime(),
        )

        const lastMonthUnderContract = listings.filter(
            listing =>
                listing.contract?.contractDate &&
                listing.contract.contractDate >= startOfMonth(lastMonth).getTime() &&
                listing.contract.contractDate <= endOfMonth(lastMonth).getTime(),
        )

        const lastMonthNewListings = listings.filter(
            listing =>
                listing.statusLog?.offline &&
                listing.statusLog.offline >= startOfMonth(lastMonth).getTime() &&
                listing.statusLog.offline <= endOfMonth(lastMonth).getTime(),
        )

        return {
            thisMonth: { newListings: thisMonthNewListings, underContract: thisMonthUnderContract },
            lastMonth: { newListings: lastMonthNewListings, underContract: lastMonthUnderContract },
        }
    }

    const [month, setMonth] = useState(new Date())
    const addMonth = (amount: number) => () => setMonth(addMonths(month, amount))

    const [thisMonth, setThisMonth] = useState<ListingsSummary>()
    const [lastMonth, setLastMonth] = useState<ListingsSummary>()

    useEffect(() => {
        const fetchData = async () => {
            try {
                setThisMonth(undefined)
                setLastMonth(undefined)

                const { thisMonth: thisMonthData, lastMonth: lastMonthData } =
                    await getListingsForBothMonths(month)
                setThisMonth(thisMonthData)
                setLastMonth(lastMonthData)
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        }

        fetchData()
    }, [month])

    const currentMonthContracts = thisMonth?.underContract

    const loading = !thisMonth || !lastMonth

    return (
        <Box sx={{ padding: 4 }}>
            <Stack
                direction="row"
                spacing={2}
                mt={1}
                mb={2}
            >
                <IconButton onClick={addMonth(-1)}>
                    <ArrowBackRounded />
                </IconButton>
                <Box
                    sx={{
                        width: '260px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography level="h2">{format(month, 'MMMM yyyy')}</Typography>
                </Box>
                <IconButton onClick={addMonth(1)}>
                    <ArrowForwardRounded />
                </IconButton>
            </Stack>
            {loading && <Loading />}
            {!loading && (
                <>
                    <SummarySection soldListings={currentMonthContracts!} />
                    <MainTable listings={currentMonthContracts!} />

                    <MobileFriendlyStack
                        sx={{
                            width: '100%',
                            alignItems: 'flex-start !important',
                        }}
                    >
                        <MonthSummary
                            listings={lastMonth}
                            month={addMonths(month, -1)}
                        />
                        <MonthSummary
                            listings={thisMonth}
                            month={month}
                        />
                    </MobileFriendlyStack>
                </>
            )}
        </Box>
    )
}
